import axios from 'axios'
import {getClientVersionHeaders} from '@/config/appVersion'
import {AUTH_EXPIRED_EVENT, getPreferredDevLoginAccount, isDevAutoLoginEnabled} from '@/config/devAuth'
import {getTrackingHeaders, trackApiResult} from '@/services/tracker'
import {requestVersionRefresh} from '@/services/versionGuard'
import {normalizeUserMessage} from '@/utils/displayText'

const http = axios.create({
    baseURL: '/api',
    timeout: 15000,
    withCredentials: true,
})

const authRecoveryHttp = axios.create({
    baseURL: '/api',
    timeout: 15000,
    withCredentials: true,
})

let devLoginPromise = null

function normalizeErrorMessage(error, fallback) {
    let message = error?.response?.data?.message || error.message || fallback
    // 网络库错误需要转成中文，避免页面直接暴露英文超时或连接异常。
    if (error.code === 'ECONNABORTED' || /timeout/i.test(message)) {
        message = '接口请求超时，请稍后重试或检查后端服务状态。'
    } else if (/network error/i.test(message)) {
        message = '网络连接异常，请确认后端服务和本地代理已启动。'
    } else if (/request failed with status code 404/i.test(message)) {
        message = '接口不存在或当前后端版本未包含该能力，请确认服务已使用最新代码启动。'
    } else if (/request failed with status code 5\d\d/i.test(message)) {
        message = '后端服务异常，请查看服务日志。'
    } else if (/request failed with status code 401/i.test(message)) {
        message = '登录已失效，请重新登录。'
    }
    return normalizeUserMessage(message, fallback)
}

function buildApiError(message, authExpired = false, details = {}) {
    const error = new Error(message)
    error.authExpired = authExpired
    error.status = details.status || null
    error.code = details.code || ''
    error.retryable = details.retryable === true
    return error
}

function isRetryableTransportError(error) {
    const status = error?.response?.status
    return [502, 503, 504].includes(status)
        || error?.code === 'ECONNABORTED'
        || /timeout|network error/i.test(error?.message || '')
}

function isAuthApi(config) {
    const url = config?.url || ''
    return url.includes('/auth/login') || url.includes('/auth/dev-direct-login') || url.includes('/auth/logout')
}

function emitAuthExpired(message) {
    if (typeof window === 'undefined') {
        return
    }
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, {
        detail: {
            message: message || '登录状态已失效，请重新登录。',
        },
    }))
}

async function loginByDevAccount() {
    if (!devLoginPromise) {
        devLoginPromise = (async () => {
            const account = getPreferredDevLoginAccount()
            // 开发会话恢复与账号切换统一走后端 dev 专用直登，不再依赖或传输本地保存的密码。
            const response = await authRecoveryHttp.post('/auth/dev-direct-login', {
                username: account.username,
            })
            const result = response.data || {}
            if (result.code !== 200) {
                throw buildApiError(normalizeUserMessage(result.message, '开发环境自动登录失败，请重新登录。'), true)
            }
            return result.data
        })().finally(() => {
            devLoginPromise = null
        })
    }
    return devLoginPromise
}

async function recoverDevSessionAndRetry(config, message) {
    if (!config || config.__qcyyAuthRetry || isAuthApi(config) || !isDevAutoLoginEnabled()) {
        emitAuthExpired(message)
        return Promise.reject(buildApiError(message, true))
    }

    config.__qcyyAuthRetry = true
    try {
        // 开发阶段后端重启或 Cookie 被覆盖时，自动恢复一次登录态并重放原请求。
        await loginByDevAccount()
        return await http(config)
    } catch (error) {
        const recoveryMessage = normalizeErrorMessage(error, '开发环境自动恢复登录失败，请重新登录。')
        emitAuthExpired(recoveryMessage)
        return Promise.reject(buildApiError(recoveryMessage, true))
    }
}

function attachTracking(config) {
    config.headers = Object.assign({}, config.headers || {}, getTrackingHeaders(), getClientVersionHeaders())
    config.__qcyyStartedAt = Date.now()
    return config
}

http.interceptors.request.use(attachTracking)
authRecoveryHttp.interceptors.request.use(attachTracking)

http.interceptors.response.use(
    (response) => {
        if (response.config && ['blob', 'arraybuffer'].includes(response.config.responseType)) {
            trackApiResult(response.config, 'SUCCESS')
            return response
        }
        const result = response.data
        // 后端统一返回 Result<T> 格式，code=200 表示业务成功
        if (result.code === 200) {
            trackApiResult(response.config, 'SUCCESS')
            return result.data
        }
        if (result.code === 401) {
            const message = normalizeUserMessage(result.message, '登录状态已失效，请重新登录。')
            trackApiResult(response.config, 'FAILED', {
                errorCode: result.code,
                errorMessage: message,
            })
            return recoverDevSessionAndRetry(response.config, message)
        }
        if (result.code === 426) {
            // 后端发现前端低于最低兼容版本时，统一要求用户刷新到最新管理后台。
            const message = normalizeUserMessage(result.message, '当前页面版本过旧，请刷新后继续使用。')
            trackApiResult(response.config, 'FAILED', {
                errorCode: result.code,
                errorMessage: message,
            })
            requestVersionRefresh(message, {force: true})
            return Promise.reject(buildApiError(message))
        }
        // 业务错误，code 非 200
        trackApiResult(response.config, 'FAILED', {
            errorCode: result.code,
            errorMessage: normalizeUserMessage(result.message, '接口请求失败，请稍后重试'),
        })
        return Promise.reject(buildApiError(normalizeUserMessage(result.message, '接口请求失败，请稍后重试')))
    },
    (error) => {
        const message = normalizeErrorMessage(error, '接口请求失败，请稍后重试')
        trackApiResult(error.config || {}, 'FAILED', {
            errorCode: error?.response?.status || error.code || '',
            errorMessage: message,
        })
        if (error?.response?.status === 401) {
            return recoverDevSessionAndRetry(error.config, message)
        }
        if (error?.response?.status === 426) {
            // 兼容后端直接返回 HTTP 426 的情况，避免旧页面继续发起不兼容请求。
            requestVersionRefresh(message || '当前页面版本过旧，请刷新后继续使用。', {force: true})
        }
        return Promise.reject(buildApiError(message, false, {
            status: error?.response?.status,
            code: error?.code,
            retryable: isRetryableTransportError(error),
        }))
    }
)

export default http
